"use client"

import { useCallback, useEffect, useState } from "react"
import { useTranslations } from "next-intl"
import { X, ChevronDown } from "lucide-react"
import type { SVGProps } from "react"

function MenuIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M4 5h16" />
      <path d="M4 12h16" />
      <path d="M4 19h16" />
    </svg>
  )
}

import { Link, usePathname } from "@/lib/i18n/navigation"
import { useUIStore } from "@/lib/stores/ui"

import { Sheet, SheetTrigger, SheetContent, SheetClose } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import LanguageSwitcher from "@themes/naturstore/components/layout/LanguageSwitcher"
import config from "@themes/naturstore/config"

import type { Category } from "@/lib/graphql/types"
import type { Brand } from "@/lib/graphql/brands"
import {
  MENU_GROUPS,
  DIRECT_LINKS,
  DYNAMIC_GROUPS,
  PRIMARY_LINKS,
} from "@themes/naturstore/components/layout/nav-config"

/** Dark frame variables — matches NaturStore header */
const darkFrameVars = {
  "--background": "var(--ns-color-dark)",
  "--foreground": "var(--ns-color-light)",
  "--muted": "var(--ns-color-mid)",
  "--muted-foreground": "color-mix(in srgb, var(--ns-color-light) 70%, transparent)",
  "--accent": "var(--ns-color-mid)",
  "--accent-foreground": "var(--ns-color-light)",
  "--primary": "var(--ns-color-light)",
  "--primary-foreground": "var(--ns-color-dark)",
  "--border": "var(--ns-color-mid)",
  "--input": "var(--ns-color-mid)",
  "--ring": "var(--ns-color-light)",
} as React.CSSProperties

interface MobileMenuProps {
  categoriesSlot: React.ReactNode
}

/**
 * NaturStore mobile menu — bigger hamburger icon, no account link
 * (account is already in the primary header).
 * The trigger renders immediately; categories stream in via categoriesSlot.
 */
export default function MobileMenu({ categoriesSlot }: MobileMenuProps) {
  const t = useTranslations("layout")
  const pathname = usePathname()
  const { isMobileMenuOpen, openMobileMenu, closeMobileMenu } = useUIStore()

  useEffect(() => {
    if (isMobileMenuOpen) {
      closeMobileMenu()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only close on route change, not on menu toggle
  }, [pathname, closeMobileMenu])

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        openMobileMenu()
      } else {
        closeMobileMenu()
      }
    },
    [openMobileMenu, closeMobileMenu],
  )

  const handleLinkClick = useCallback(() => {
    closeMobileMenu()
  }, [closeMobileMenu])

  return (
    <Sheet open={isMobileMenuOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            className="min-h-11 !size-auto p-0"
            aria-label={t("header.menu")}
          />
        }
      >
        <MenuIcon className="size-[22px]" />
      </SheetTrigger>
      <SheetContent
        side="left"
        showCloseButton={false}
        className="bg-[var(--background)] text-[var(--foreground)]"
        style={darkFrameVars}
      >
        {/* Close row — height matches --header-h (set by HeaderShell's ResizeObserver)
            so the X always lands where the hamburger was, EventsBar or not. */}
        <div
          className="flex shrink-0 items-center px-4"
          style={{ height: "var(--header-h, 4.5rem)" }}
        >
          <SheetClose
            render={
              <Button
                variant="ghost"
                size="icon"
                className="min-h-11 !size-auto p-0"
                aria-label={t("header.closeMenu")}
              />
            }
          >
            <X className="size-5" />
          </SheetClose>
        </div>

        {/* The nav owns the full height between close row and footer; the
            brand symbol sits BEHIND it as a centered backdrop instead of
            reserving its own block below (which squeezed the list and
            caused the client-reported clipping in the first place). */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <div
              className="h-80 w-80 bg-[var(--foreground)] opacity-[0.035]"
              style={{
                maskImage: "url(/themes/naturstore/assets/symbol.svg)",
                WebkitMaskImage: "url(/themes/naturstore/assets/symbol.svg)",
                maskSize: "contain",
                WebkitMaskSize: "contain",
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",
                maskPosition: "center",
                WebkitMaskPosition: "center",
              }}
            />
          </div>

          <nav className="relative min-h-0 flex-1 overflow-y-auto px-4 pb-6">
            <div className="flex flex-col gap-1">
              {categoriesSlot}
              <Link
                href="/products"
                onClick={handleLinkClick}
                className="rounded-md px-3 py-2.5 text-sm font-medium uppercase tracking-wide transition-colors hover:bg-muted"
              >
                {t("header.shopAll")}
              </Link>
              {config.features.consultations && (
                <Link
                  href="/consultations"
                  onClick={handleLinkClick}
                  className="mt-4 flex items-center justify-between border-t border-[var(--foreground)]/10 px-3 pt-5 pb-2 text-sm font-medium uppercase tracking-wide transition-colors hover:text-[var(--foreground)]"
                >
                  {t("header.nav.consultations")}
                  <span aria-hidden="true" className="text-sm text-[var(--foreground)]/30">
                    →
                  </span>
                </Link>
              )}
            </div>
          </nav>
        </div>
        <div className="flex flex-col gap-4 border-t p-4">
          <LanguageSwitcher />
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Category nav links — rendered inside the drawer via a slot so
 * the hamburger trigger doesn't wait for the categories fetch.
 */
export function MobileMenuCategories({
  categories,
  brands,
}: {
  categories: Category[]
  brands: Brand[]
}) {
  const tNav = useTranslations("layout.header.nav")
  const { closeMobileMenu } = useUIStore()

  const handleLinkClick = useCallback(() => {
    closeMobileMenu()
  }, [closeMobileMenu])

  const catBySlug = new Map<string, Category>()
  for (const cat of categories) {
    catBySlug.set(cat.slug, cat)
  }

  return (
    <>
      {/* Brands first — matches desktop nav order. */}
      {PRIMARY_LINKS.map((item) => (
        <Link
          key={item.labelKey}
          href={item.href}
          onClick={handleLinkClick}
          className="rounded-md px-3 py-2.5 text-sm font-medium uppercase tracking-wide transition-colors hover:bg-muted"
        >
          {tNav(item.labelKey)}
        </Link>
      ))}
      {DYNAMIC_GROUPS.map((group) => {
        if (group.kind === "brands" && brands.length > 0) {
          return (
            <MobileMenuBrands
              key={group.labelKey}
              label={tNav(group.labelKey)}
              brands={brands}
              onLinkClick={handleLinkClick}
            />
          )
        }
        return null
      })}
      {MENU_GROUPS.map((group) => {
        const allCats = group.slugs
          .flat()
          .map((s) => catBySlug.get(s))
          .filter(Boolean) as Category[]
        if (allCats.length === 0) return null

        const allChildren = allCats.flatMap((cat) => cat.children)

        if (allChildren.length === 0) {
          return (
            <Link
              key={group.labelKey}
              href={`/category/${allCats[0].slug}`}
              onClick={handleLinkClick}
              className="rounded-md px-3 py-2.5 text-sm font-medium uppercase tracking-wide transition-colors hover:bg-muted"
            >
              {tNav(group.labelKey)}
            </Link>
          )
        }

        return (
          <MobileMenuGroup
            key={group.labelKey}
            label={tNav(group.labelKey)}
            categories={allCats}
            banner={
              group.banner ? (
                <MobileMenuBanner
                  href={group.banner.href}
                  title={tNav(group.banner.titleKey)}
                  description={tNav(group.banner.descriptionKey)}
                  testId={`menu-banner-${group.labelKey}`}
                  onLinkClick={handleLinkClick}
                />
              ) : null
            }
            onLinkClick={handleLinkClick}
          />
        )
      })}
      {DIRECT_LINKS.map((item) => (
        <Link
          key={item.labelKey}
          href={item.href ?? `/category/${item.slug}`}
          onClick={handleLinkClick}
          className="rounded-md px-3 py-2.5 text-sm font-medium uppercase tracking-wide transition-colors hover:bg-muted"
        >
          {tNav(item.labelKey)}
        </Link>
      ))}
    </>
  )
}

function MobileMenuBanner({
  href,
  title,
  description,
  testId,
  onLinkClick,
}: {
  href: string
  title: string
  description: string
  testId?: string
  onLinkClick: () => void
}) {
  return (
    <Link
      href={href}
      onClick={onLinkClick}
      data-testid={testId}
      className="group mt-2 flex items-center justify-between gap-3 border-t border-[var(--foreground)]/10 px-3 pt-4 pb-2"
    >
      <span className="flex flex-col gap-1">
        <span className="text-[11px] font-normal uppercase tracking-[0.15em] text-[var(--foreground)]/85 transition-colors duration-500 group-hover:text-[var(--foreground)]">
          {title}
        </span>
        <span className="text-[13px] font-light text-[var(--foreground)]/50">{description}</span>
      </span>
      <span
        aria-hidden="true"
        className="text-sm text-[var(--foreground)]/30 transition-all duration-500 group-hover:translate-x-0.5 group-hover:text-[var(--foreground)]/70"
      >
        →
      </span>
    </Link>
  )
}

function MobileMenuBrands({
  label,
  brands,
  onLinkClick,
}: {
  label: string
  brands: Brand[]
  onLinkClick: () => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const submenuId = "mobile-submenu-brands"

  // Bucket brands by first letter so the accordion has scannable dividers.
  const groups = new Map<string, Brand[]>()
  for (const brand of brands) {
    const first = brand.label.charAt(0).toUpperCase()
    const key = /[A-Z]/.test(first) ? first : "#"
    const existing = groups.get(key)
    if (existing) existing.push(brand)
    else groups.set(key, [brand])
  }
  const entries = Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b))

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium uppercase tracking-wide transition-colors hover:bg-muted"
        aria-expanded={isExpanded}
        aria-controls={submenuId}
      >
        {label}
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground motion-safe:transition-transform motion-safe:duration-200 ${isExpanded ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {isExpanded && (
        <div id={submenuId} className="ml-3 flex flex-col border-l pl-3">
          {entries.map(([letter, items]) => (
            <div
              key={letter}
              className="flex flex-col pt-4 not-first:mt-4 not-first:border-t not-first:border-[var(--foreground)]/10"
            >
              <span
                aria-hidden="true"
                className="mx-3 pb-2 font-[family-name:var(--ns-font-heading)] text-base leading-none text-[var(--foreground)]/70"
              >
                {letter}
              </span>
              {items.map((brand) => (
                <Link
                  key={brand.id}
                  href={`/products?brand=${brand.id}`}
                  onClick={onLinkClick}
                  className="rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted"
                >
                  {brand.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function MobileMenuGroup({
  label,
  categories,
  banner,
  onLinkClick,
}: {
  label: string
  categories: Category[]
  banner?: React.ReactNode
  onLinkClick: () => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const submenuId = `mobile-submenu-${categories.map((c) => c.id).join("-")}`

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium uppercase tracking-wide transition-colors hover:bg-muted"
        aria-expanded={isExpanded}
        aria-controls={submenuId}
      >
        {label}
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground motion-safe:transition-transform motion-safe:duration-200 ${isExpanded ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {isExpanded && (
        <div id={submenuId} className="ml-3 flex flex-col gap-0.5 border-l pl-3">
          {categories.map((cat) => (
            <div key={cat.id} className="flex flex-col gap-0.5">
              {/* Category header */}
              <Link
                href={`/category/${cat.slug}`}
                onClick={onLinkClick}
                className="rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {cat.name}
              </Link>
              {cat.children.map((child) => (
                <Link
                  key={child.id}
                  href={`/category/${child.slug}`}
                  onClick={onLinkClick}
                  className="rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted"
                >
                  {child.name}
                </Link>
              ))}
            </div>
          ))}
          {banner}
        </div>
      )}
    </div>
  )
}
